import {
  faCaretDown,
  faCircleMinus,
  faMoneyBillSimpleWave
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { PropsWithChildren } from "react";
import {
  type AllowanceDetail,
  type HybridBillingDiscount
} from "@kanpla/api-contract";

import { T } from "@repo/transifex";
import { cn } from "~/lib/utils";
import { type Currency } from "~/api/products/types";
import { priceFormatter } from "~/utils/price-formatter";

import { Popover } from "./Popover";

export type AllowanceChipProps = {
  allowances: AllowanceDetail[];
  currency: Currency;
  locale: string;
  discounts: HybridBillingDiscount[];
};

export const AllowanceChip = ({
  allowances,
  currency,
  locale,
  discounts
}: AllowanceChipProps) => {
  if (allowances.length === 0) return null;

  if (allowances.length === 1) {
    return (
      <SingleAllowanceChip
        allowance={allowances[0]}
        currency={currency}
        locale={locale}
      />
    );
  }

  return (
    <MultiAllowanceChip
      allowances={allowances}
      currency={currency}
      locale={locale}
      discounts={discounts}
    />
  );
};

type ChipPillProps = PropsWithChildren<{
  exhausted: boolean;
  caret?: "up" | "down";
}>;

const ChipPill = ({ exhausted, caret, children }: ChipPillProps) => (
  <div
    className={cn(
      "flex h-[72px] items-center gap-3 rounded-full px-6 text-2xl",
      exhausted
        ? "bg-interface-neutral-2 text-interface-neutral-5"
        : "bg-interface-positive-1 text-interface-neutral-6"
    )}
  >
    <FontAwesomeIcon
      icon={exhausted ? faCircleMinus : faMoneyBillSimpleWave}
      className="text-[28px]"
    />
    {children}
    {caret !== undefined ? (
      <FontAwesomeIcon
        icon={faCaretDown}
        className={cn("text-xl transition-transform duration-200", {
          "rotate-180": caret === "up"
        })}
      />
    ) : null}
  </div>
);

type AllowanceLabelProps = {
  allowance: AllowanceDetail;
  currency: Currency;
  locale: string;
};

const AllowanceLabel = ({
  allowance,
  currency,
  locale
}: AllowanceLabelProps) => {
  if (allowance.type === "unitPrice") {
    return (
      <span>
        <strong>
          {priceFormatter({ value: allowance.remaining, currency, locale })}
        </strong>{" "}
        <T _str="allowance left" />{" "}
        <PeriodLabel period={allowance.period} variant="chip" />
      </span>
    );
  }

  return (
    <span>
      <strong>{allowance.remaining}</strong>{" "}
      {allowance.remaining === 1 ? (
        <T _str="product in allowance left" />
      ) : (
        <T _str="products in allowance left" />
      )}{" "}
      <PeriodLabel period={allowance.period} variant="chip" />
    </span>
  );
};

const SingleAllowanceChip = ({
  allowance,
  currency,
  locale
}: AllowanceLabelProps) => (
  <ChipPill exhausted={allowance.isAllowanceOver}>
    <AllowanceLabel allowance={allowance} currency={currency} locale={locale} />
  </ChipPill>
);

const MultiAllowanceChip = ({
  allowances,
  currency,
  locale,
  discounts
}: AllowanceChipProps) => {
  const allExhausted = allowances.every((a) => a.isAllowanceOver);
  const discountNameById = Object.fromEntries(
    discounts.map((d) => [d.id, d.name])
  );

  return (
    <Popover
      align="left"
      position="bottom"
      dropdownClassName="rounded-3xl shadow-2xl"
      content={
        <div className="min-w-[480px] overflow-hidden rounded-3xl bg-white">
          <div className="max-h-96 overflow-y-auto">
            {allowances.map((allowance) => (
              <AllowanceDrawerRow
                key={allowance.id}
                allowance={allowance}
                currency={currency}
                locale={locale}
                name={discountNameById[allowance.discountId]}
              />
            ))}
          </div>
        </div>
      }
    >
      {(open) => (
        <ChipPill exhausted={allExhausted} caret={open ? "up" : "down"}>
          {allExhausted ? (
            <T _str="No Allowance left" />
          ) : (
            <T _str="Allowance available" />
          )}
        </ChipPill>
      )}
    </Popover>
  );
};

type PeriodLabelProps = {
  period: AllowanceDetail["period"];
  variant: "chip" | "drawer";
};

const PeriodLabel = ({ period, variant }: PeriodLabelProps) => {
  if (variant === "chip") {
    switch (period) {
      case "DAILY__RRULE_STRING":
        return <T _str="today" />;
      case "WEEKLY__RRULE_STRING":
        return <T _str="this week" />;
      case "BIWEEKLY__RRULE_STRING":
        return <T _str="these two weeks" />;
      case "MONTHLY__RRULE_STRING":
      case "MONTHLY_END_RRULE_STRING":
        return <T _str="this month" />;
      default:
        return null;
    }
  }

  switch (period) {
    case "DAILY__RRULE_STRING":
      return <T _str="resets each day" />;
    case "WEEKLY__RRULE_STRING":
      return <T _str="resets each week" />;
    case "BIWEEKLY__RRULE_STRING":
      return <T _str="resets every two weeks" />;
    case "MONTHLY__RRULE_STRING":
    case "MONTHLY_END_RRULE_STRING":
      return <T _str="resets each month" />;
    default:
      return null;
  }
};

type AllowanceDrawerRowProps = {
  allowance: AllowanceDetail;
  currency: Currency;
  locale: string;
  name?: string;
};

const AllowanceDrawerRow = ({
  allowance,
  currency,
  locale,
  name
}: AllowanceDrawerRowProps) => (
  <div
    className={cn(
      "flex items-center gap-5 px-6 py-5 last:pb-6 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-interface-neutral-3",
      { "opacity-40": allowance.isAllowanceOver }
    )}
  >
    <div
      className={cn(
        "flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-white",
        allowance.type === "unitPrice"
          ? "bg-interface-positive-3"
          : "bg-interface-neutral-6"
      )}
    >
      <FontAwesomeIcon icon={faMoneyBillSimpleWave} className="text-2xl" />
    </div>

    <div className="flex flex-1 flex-col">
      <span className="text-2xl font-bold text-interface-neutral-6">
        {name ?? <T _str="Allowance" />}
      </span>
      <span className="text-base text-interface-neutral-5">
        <DrawerSubtitle allowance={allowance} />
      </span>
    </div>

    <span className="text-2xl font-bold text-interface-neutral-6">
      <DrawerValue allowance={allowance} currency={currency} locale={locale} />
    </span>
  </div>
);

const TypeLabel = ({ allowance }: { allowance: AllowanceDetail }) => {
  if (allowance.type === "unitPrice") return <T _str="Money" />;
  return (
    <>
      {allowance.remaining}{" "}
      <T _str={allowance.remaining === 1 ? "product" : "products"} />
    </>
  );
};

const DrawerSubtitle = ({ allowance }: { allowance: AllowanceDetail }) => (
  <>
    <TypeLabel allowance={allowance} />
    {" · "}
    <PeriodLabel period={allowance.period} variant="drawer" />
  </>
);

const DrawerValue = ({
  allowance,
  currency,
  locale
}: {
  allowance: AllowanceDetail;
  currency: Currency;
  locale: string;
}) => {
  if (allowance.type === "unitPrice") {
    return priceFormatter({ value: allowance.remaining, currency, locale });
  }
  return <>{allowance.remaining}×</>;
};
